import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * PPTX 在线预览（pptxviewjs + canvas）。
 * 旧版 .ppt 无法在浏览器解析，由外层走下载兜底。
 */
export default function PlatformTutorialPptxPreview({
  url = '',
  onError,
}) {
  const canvasRef = useRef(null);
  const viewerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);

  useEffect(() => {
    let alive = true;
    let viewer = null;

    const run = async () => {
      setLoading(true);
      setError('');
      setSlideIndex(0);
      setSlideCount(0);
      const canvas = canvasRef.current;
      if (!url || !canvas) return;

      try {
        const stage = canvas.parentElement;
        if (stage) {
          const w = Math.max(320, stage.clientWidth - 24);
          const h = Math.max(240, stage.clientHeight - 24);
          canvas.width = w;
          canvas.height = h;
        }

        const mod = await import('pptxviewjs');
        const PPTXViewer = mod.PPTXViewer || mod.default?.PPTXViewer;
        if (!PPTXViewer) throw new Error('PPTX 预览组件加载失败');

        viewer = new PPTXViewer({
          canvas,
          slideSizeMode: 'fit',
          backgroundColor: '#111827',
        });
        viewerRef.current = viewer;

        const res = await fetch(url);
        if (!res.ok) throw new Error(`加载失败（${res.status}）`);
        const buffer = await res.arrayBuffer();
        await viewer.loadFile(buffer);
        await viewer.render(canvas);

        if (!alive) return;
        setSlideCount(viewer.getSlideCount() || 0);
        setSlideIndex(viewer.getCurrentSlideIndex() || 0);
      } catch (e) {
        if (!alive) return;
        const message = e?.message || 'PPTX 预览失败';
        setError(message);
        onError?.(message);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();

    return () => {
      alive = false;
      try {
        viewer?.destroy?.();
      } catch {
        /* ignore */
      }
      viewerRef.current = null;
    };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps -- onError 为可选回调，避免父组件重渲染导致重复加载

  const goPrev = async () => {
    const viewer = viewerRef.current;
    if (!viewer || !canvasRef.current) return;
    await viewer.previousSlide(canvasRef.current);
    setSlideIndex(viewer.getCurrentSlideIndex() || 0);
  };

  const goNext = async () => {
    const viewer = viewerRef.current;
    if (!viewer || !canvasRef.current) return;
    await viewer.nextSlide(canvasRef.current);
    setSlideIndex(viewer.getCurrentSlideIndex() || 0);
  };

  return (
    <div className="pt-preview__pptx">
      <div className="pt-preview__pptx-stage">
        <canvas ref={canvasRef} className="pt-preview__pptx-canvas" />
        {loading ? <div className="pt-preview__pptx-status">演示文稿加载中…</div> : null}
        {!loading && error ? <div className="pt-preview__pptx-status">{error}</div> : null}
      </div>
      {!loading && !error && slideCount > 0 ? (
        <div className="pt-preview__pptx-nav">
          <button
            type="button"
            className="pt-preview__pptx-nav-btn"
            onClick={goPrev}
            disabled={slideIndex <= 0}
          >
            <ChevronLeft size={16} aria-hidden />
            上一页
          </button>
          <span className="pt-preview__pptx-page">
            {slideIndex + 1} / {slideCount}
          </span>
          <button
            type="button"
            className="pt-preview__pptx-nav-btn"
            onClick={goNext}
            disabled={slideIndex >= slideCount - 1}
          >
            下一页
            <ChevronRight size={16} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}
